import React from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Mail, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { CONTACT_INFO } from "@/data/companies";

const details = [
  { icon: MapPin, label: "Location", value: CONTACT_INFO.location },
  { icon: Mail, label: "Email", value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
  { icon: Clock, label: "Hours", value: CONTACT_INFO.hours },
];

export default function Contact() {
  const [params] = useSearchParams();
  const presetCategory = params.get("category") || "";

  return (
    <div>
      <section className="bg-slate-50 pt-32 md:pt-44 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">
              Contact
            </p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight max-w-3xl">
              Let&apos;s start a conversation
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Tell us a little about what you need and the right team will get
              back to you. We typically respond within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
                Get in touch
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Reach out and the right AMS team will follow up within one
                business day.
              </p>
              <div className="mt-8 space-y-6">
                {details.map((d) => (
                  <div key={d.label} className="flex items-start gap-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 shrink-0">
                      <d.icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">
                        {d.label}
                      </p>
                      {d.href ? (
                        <a
                          href={d.href}
                          data-testid="contact-email-link"
                          className="text-slate-900 hover:text-blue-600 transition-colors"
                        >
                          {d.value}
                        </a>
                      ) : (
                        <p className="text-slate-900">{d.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7" id="contact-form">
            <Reveal delay={1}>
              <ContactForm presetCategory={presetCategory} presetNonce={presetCategory ? 1 : 0} />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
